import React from 'react';
import _ from 'underscore';

import BenchlingConfig from 'BenchlingConfig';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import RequestAssignees from 'requests/assignees/RequestAssignees';
import {RequestSampleStatusCounts} from 'requests/fulfillments/FulfillmentConstants';
import {getLegacyRequestObjectName, Request, RequestSchema} from 'requests/RequestConstants';
import sortRequestTaskSchemas from 'requests/schemas/sortRequestTaskSchemas';
import {RequestTaskSchema} from 'requests/tasks/RequestTaskConstants';
import * as ExpandedTableTagUtil from 'schemas/tags/ExpandedTableTagUtil';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ScrollableTableColumn} from 'ui-library/scrollable/ScrollableTableConstants';
import DateTableCell from 'ui-library/scrollable/table-cells/DateTableCell';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import {BasicEvent} from 'util/Types';

import RequestStatusLabel from './RequestStatusLabel';
import SampleStatusCounts from './SampleStatusCounts';

export interface RequestListingExtras {
  sampleStatusCountsByRequestId?: {[requestId: string]: RequestSampleStatusCounts};
  requestedTaskSchemaIdsByRequestId?: {[requestId: string]: Array<string>};
  requestTaskSchemasById?: {[schemaId: string]: RequestTaskSchema} | null;
  requestSchemas?: Array<RequestSchema> | null;
}
export type RequestColumnExtras = Pick<
  RequestListingExtras,
  | 'sampleStatusCountsByRequestId'
  | 'requestedTaskSchemaIdsByRequestId'
  | 'requestTaskSchemasById'
  | 'requestSchemas'
>;

export default class RequestListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.REQUEST> {
  listingRendererType = ListingRendererType.REQUEST;

  renderIcon(): JSX.Element {
    return <BenchlingIcon icon="request" className="scrollableTable-cell-icon" />;
  }

  getNonPluginColumnConfigurations(
    requests: Array<Request & {schema: RequestSchema}>,
    {
      sampleStatusCountsByRequestId,
      requestedTaskSchemaIdsByRequestId,
      requestTaskSchemasById,
      requestSchemas,
    }: RequestListingExtras
  ): Array<ColumnConfiguration<Request>> {
    let columns: Array<ColumnConfiguration<Request>> = _.compact([
      {
        column: {
          accessorKey: ColumnAccessorKey.NAME,
          header: `${getLegacyRequestObjectName()} ID`,
          flexGrow: 1,
        },
        render: (request: Request) => request.name,
      },
      {
        column: {accessorKey: ColumnAccessorKey.STATUS, header: 'Status', flexGrow: 1},
        render: (request: Request) => <RequestStatusLabel requestStatus={request.requestStatus} />,
      },
      sampleStatusCountsByRequestId && {
        column: {accessorKey: ColumnAccessorKey.SAMPLE_STATUS_COUNTS, header: 'Samples', flexGrow: 1},
        render: (request: Request) => (
          <SampleStatusCounts statusCounts={sampleStatusCountsByRequestId[request.id]} />
        ),
      },
      requestedTaskSchemaIdsByRequestId &&
        requestTaskSchemasById && {
          column: {accessorKey: ColumnAccessorKey.REQUESTED_TASK_SCHEMAS, header: 'Tasks', flexGrow: 1},
          render: (request: Request) => {
            const requestedTaskSchemas = sortRequestTaskSchemas(
              requestedTaskSchemaIdsByRequestId[request.id].map(
                (taskSchemaId: string) => requestTaskSchemasById[taskSchemaId]
              )
            );
            return requestedTaskSchemas.map((taskSchema) => taskSchema.name).join(', ');
          },
        },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (request: Request) => (
          <DatetimeTableCell datetime={request.createdAt} showTimeInCell={false} />
        ),
      },
      BenchlingConfig.REQUESTS_ENABLE_REQUESTOR
        ? {
            column: {
              accessorKey: ColumnAccessorKey.REQUESTS_REQUESTOR,
              header: 'Requestor',
              flexGrow: 1,
            },
            render: (request: Request) => getPartyDisplayString(request.requestor),
          }
        : {
            column: {
              accessorKey: ColumnAccessorKey.CREATOR,
              header: 'Creator',
              flexGrow: 1,
            },
            render: (request: Request) => getPartyDisplayString(request.creator),
          },
      {
        column: {accessorKey: ColumnAccessorKey.ASSIGNEES, header: 'Assigned To', flexGrow: 1},
        render: (request: Request) => <RequestAssignees assignees={request.assignees} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.SCHEDULED_ON,
          header: getColumnHeaderFromSortKey(SortKey.SORT_SCHEDULED_ON_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_SCHEDULED_ON_KEY,
        },
        render: (request: Request) => <DateTableCell date={request.scheduledOn} />,
      },
    ]);
    if (requestSchemas) {
      // The methods in ExpandedTableTagUtil expect all items to have a `schema` field, so we need
      // to add one to each request
      const requestsWithSchema: Array<Request & {schema: RequestSchema}> = requests
        .map((request: Request) => ({
          ...request,
          schema: _.findWhere(requestSchemas, {id: request.schemaId}),
        }))
        .filter(
          (requestWithOptionalSchema: Request & {schema: RequestSchema | undefined}) =>
            requestWithOptionalSchema.schema != null
        ) as Array<Request & {schema: RequestSchema}>;
      const tagColumns = ExpandedTableTagUtil.listSortedTagColumns(requestsWithSchema);
      if (tagColumns.length) {
        columns = columns.concat(
          tagColumns.map((column: ScrollableTableColumn) => ({
            column,
            render: (request: Request) => {
              const tagsByColumn = ExpandedTableTagUtil.mapTagsToColumns({
                tags: request.tags,
                tagColumns: [column],
              });
              return tagsByColumn.tagFields[column.accessorKey];
            },
          }))
        );
      }
    }
    return columns;
  }

  getNonPluginRowContentHeight(request: Request): number {
    return ExpandedTableTagUtil.getRowHeight(request.tags);
  }

  openContextMenu(request: Request, e: BasicEvent, extras: unknown): void {
    // Not yet implemented
  }
}
